import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import TableLayout from '../global/Table/TableLayout';
import { NotificationTemplatesTableColumns } from './Table/NotificationTemplatesTableColumns';
import NotificationTemplatesTableRow from './Table/NotificationTemplatesTableRow';

import useTableActions from '../../hooks/common/useTableActions';
import { useGetNotificationTemplates } from '../../hooks/notificationTemplates/useGetNotificationTemplates';
import { useDeleteNotificationTemplate } from '../../hooks/notificationTemplates/useDeleteNotificationTemplate';
import { usePermissions } from '../../auth/PermissionsContext';

const NotificationTemplatesTable = () => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const { has } = usePermissions();
    const navigate = useNavigate();

    const perms = useMemo(
        () => ({
            list: has('notifications.manage'),
            create: has('notifications.manage'),
            edit: has('notifications.manage'),
            delete: has('notifications.manage'),
        }),
        [has]
    );

    const [channelFilter, setChannelFilter] = useState('');
    const [localeFilter, setLocaleFilter] = useState('');

    const { deleteTemplateMutate, isDeleting } = useDeleteNotificationTemplate();

    const {
        page,
        perPage,
        search,
        order,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleBulkDelete,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: '/settings/notifications',
        deleteSingle: deleteTemplateMutate,
    });

    const params = useMemo(
        () => ({
            page,
            per_page: perPage,
            code: search || undefined,
            channel: channelFilter || undefined,
            locale: localeFilter || undefined,
        }),
        [page, perPage, search, channelFilter, localeFilter]
    );

    const { templates, meta, isLoading, error, refetch } = useGetNotificationTemplates(params);

    const tableFilters = [
        {
            name: 'channel',
            label: { ar: 'القناة', en: 'Channel' },
            value: channelFilter,
            onChange: (val) => setChannelFilter(val),
            options: [
                { value: '',      label: { ar: 'الكل',         en: 'All' } },
                { value: 'email', label: { ar: 'بريد إلكتروني', en: 'Email' } },
                { value: 'sms',   label: { ar: 'رسالة نصية',    en: 'SMS' } },
                { value: 'push',  label: { ar: 'إشعار',         en: 'Push' } },
            ],
        },
        {
            name: 'locale',
            label: { ar: 'اللغة', en: 'Locale' },
            value: localeFilter,
            onChange: (val) => setLocaleFilter(val),
            options: [
                { value: '',   label: { ar: 'الكل',       en: 'All' } },
                { value: 'en', label: { ar: 'الإنجليزية', en: 'English' } },
                { value: 'ar', label: { ar: 'العربية',    en: 'Arabic' } },
            ],
        },
    ];

    const renderRow = (rowParams) => (
        <NotificationTemplatesTableRow
            key={rowParams.item.id}
            template={rowParams.item}
            index={rowParams.index}
            isSelected={selectedIds.includes(rowParams.item.id)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
        />
    );

    return (
        <TableLayout
            title={isArabic ? 'قوالب الإشعارات' : 'Notification Templates'}
            subtitle={isArabic
                ? 'تخصيص الإيميلات والرسائل المُرسلة للعملاء — بريد، SMS، وإشعارات التطبيق'
                : 'Customize the emails, SMS, and push notifications sent to customers'}
            search={search}
            onSearchChange={setSearch}
            addButtonLabel={isArabic ? 'إضافة قالب' : 'Add Template'}
            onAddClick={() => navigate('/settings/notifications/create')}
            addButtonVisible={perms.create}
            isLoading={isLoading}
            error={error}
            items={templates}
            meta={meta}
            page={page}
            perPage={perPage}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            order={order}
            onRequestSort={handleRequestSort}
            columns={NotificationTemplatesTableColumns}
            renderRow={renderRow}
            menuAnchorEl={menuAnchorEl}
            openMenu={Boolean(menuAnchorEl)}
            onMenuOpen={handleMenuOpen}
            onMenuClose={handleMenuClose}
            onMenuEdit={handleEdit}
            onMenuDelete={handleMenuDelete}
            menuDisableEdit={!perms.edit}
            menuDisableDelete={!perms.delete}
            isDeletingSingle={isDeleting}
            selectedIds={selectedIds}
            onSelectAllClick={(e) => handleSelectAllClick(e, templates)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onBulkDelete={handleBulkDelete}
            bulkDeleteDisabled={!perms.delete}
            filters={tableFilters}
            onClearAllFilters={() => {
                setChannelFilter('');
                setLocaleFilter('');
            }}
            emptyLabelAr="لا توجد قوالب"
            emptyLabelEn="No templates found"
            refetch={refetch}
        />
    );
};

export default NotificationTemplatesTable;
